import React, { useEffect } from "react";
import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import NotFound from "@/pages/not-found";
import LandingPage from "@/pages/landing-page";
import AuthPage from "@/pages/auth-page";
import GameRoom from "@/pages/game-room";
import GamesPage from "@/pages/games-page";
import ProfilePage from "@/pages/profile-page";
import { AuthProvider } from "@/hooks/use-auth";
import { BottomNav } from "@/components/ui/bottom-nav";
import MessagesPage from "@/pages/friends-page";
import ConversationPage from "@/pages/conversation-page";
import WatchPage from "./pages/watch-page";
import ChangeLog from "@/pages/change-log";
import GameHistory from "@/pages/game-history";
import SettingsPage from "./pages/settings-page";
import ThemesPage from "@/pages/themes-page";
import UserSettingsPage from "@/pages/user-settings";
import DeckMarketplace from "@/pages/deck-marketplace";
import DeckDetailPage from "@/pages/deck-detail";
import SavedDecksPage from "@/pages/saved-decks";
import Achievements from './pages/Achievements';
import TournamentsPage from '@/pages/tournaments-page';
import TournamentDetailPage from '@/pages/tournament-detail';
import ErrorBoundary from './components/ErrorBoundary';
import ReplayPage from '@/pages/replay-page';
import SupportPage from '@/pages/support-page';
import VerificationConfirmation from '@/pages/verification-confirmation';
import AdminSupportPage from '@/pages/admin-support-page';
import AdminGameRoomPage from '@/pages/admin-game-room-page';
import AdminTournamentPage from '@/pages/admin-tournament-page';
import ShopPage from '@/pages/shop-page';
import PaymentCallbackPage from '@/pages/payment-callback';
import PrivacyPolicyPage from '@/pages/privacy-policy';
import TermsOfServicePage from '@/pages/terms-of-service';
import { AchievementProvider } from './contexts/AchievementContext';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationsPage from '@/pages/notifications-page';
import { useThemeEffect } from "@/hooks/use-theme";
import AdminPage from "./pages/admin-page";
import ManagerPage from "./pages/manager-page";
import AboutPage from "@/pages/about-page";
import PasswordResetPage from "@/pages/password-reset";
import EmailChangePage from "@/pages/email-change";
import PasswordChangePage from "@/pages/password-change";
import { Loader2 } from "lucide-react";
import { initGA } from "@/lib/analytics"; // Google Analytics initialization
import { WebSocketProvider } from "@/providers/websocket-provider"; // Global WebSocket provider
import { QueueStatus } from "@/components/queue-status"; // Floating queue status indicator
import { MatchDialog } from "@/components/ui/match-dialog"; // Match acceptance dialog
import { TutorialSuggestionDialog } from "@/components/tutorial/TutorialSuggestionDialog"; // Tutorial suggestion dialog
import { checkRedirectResult } from '@/lib/firebase'; // Import Firebase auth check function
import TransactionDetailPage from '@/pages/transaction-detail'; // Import the transaction detail page
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app'; // Import statically instead of dynamically
import CardBackMarketplace from "@/pages/card-back-marketplace";
import RewardsPage from "@/pages/rewards-page";
import AllAwardsPage from "@/pages/all-awards-page";
import OpenSourceCredits from "@/pages/open-source-credits";
import { AnnouncementBannerProvider } from '@/components/announcement-banner'; // Import the AnnouncementBannerProvider
// Import the sound test components directly
import SoundTest from '@/pages/sound-test'; // Import the sound test page
import { useAuth } from "./hooks/use-auth";
// Import our app sound service
import appSound from '@/services/app-sound-service';
// Import sound utilities
import { initSoundSystem, preloadGameSounds } from '@/utils/sound-utils';
import { SoundCategory } from '@/services/mobile-audio';
// Import the DeckStorageInitializer component
import { DeckStorageInitializer } from '@/components/deck-storage-initializer';
// Import the PushNotificationListener component
import PushNotificationListener from '@/components/PushNotificationListener';

// Import the MobileProvider
import { MobileProvider } from '@/providers/mobile-provider';
import { AuthLoading } from "@/components/auth/auth-loading";

// Import the VersionCheckProvider
import { VersionCheckProvider } from '@/providers/version-check-provider';

// These components might not exist yet, so let's use dummy components for now

// Theme Provider wrapper component
function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Apply global theme
  useThemeEffect();
  return <>{children}</>;
}

// Register service worker for PWA functionality
function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => registration.unregister());
    });
  }
}

// Initialize sound system
function initializeSounds() {
  // Initialize sound system (important for browser autoplay policy)
  initSoundSystem();
  
  // Preload all necessary sound categories
  preloadGameSounds([
    SoundCategory.UI,
    SoundCategory.Gameplay,
    SoundCategory.Win,
    SoundCategory.Background
  ]).catch(err => {
    console.error('Error preloading sounds:', err);
  });
  
  // Play app launch sound
  setTimeout(() => {
    appSound.playAppLaunchSound();
    
    // Start background music after a short delay
    setTimeout(() => {
      appSound.playMenuBackgroundMusic();
    }, 1000);
  }, 500);
}

interface ProtectedRouteProps {
  component: React.ComponentType<any>;
}

const ProtectedAdminRoute = ({ component: Component }: ProtectedRouteProps): JSX.Element => {
  const { user, isLoading } = useAuth();
  
  console.log('ProtectedAdminRoute - User:', user);
  console.log('ProtectedAdminRoute - isLoading:', isLoading);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-border" />
      </div>
    );
  }
  
  if (!user) {
    console.log('ProtectedAdminRoute - No user, redirecting to auth');
    return <Redirect to="/auth" />;
  }
  
  if (user.role !== 'admin') {
    console.log('ProtectedAdminRoute - Not admin, redirecting to home');
    return <Redirect to="/" />;
  }
  
  console.log('ProtectedAdminRoute - Access granted to admin area');
  return <Component />;
};

const ProtectedManagerRoute = ({ component: Component }: ProtectedRouteProps): JSX.Element => {
  const { user, isLoading } = useAuth();
  
  console.log('ProtectedManagerRoute - User:', user);
  console.log('ProtectedManagerRoute - isLoading:', isLoading);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-border" />
      </div>
    );
  }
  
  if (!user) {
    console.log('ProtectedManagerRoute - No user, redirecting to auth');
    return <Redirect to="/auth" />;
  }
  
  if (user.role !== 'manager' && user.role !== 'admin') {
    console.log('ProtectedManagerRoute - Not manager or admin, redirecting to home');
    return <Redirect to="/" />;
  }
  
  console.log('ProtectedManagerRoute - Access granted to manager area');
  return <Component />;
};


// Import our analytics hook
import { usePageViewTracking } from "./hooks/use-analytics";
import { BoardDetailsPage } from "./components/user/BoardDetailsPage";
import DeleteAccountPage from "./components/user/delete-account";

function Router() {
  // Initialize page view tracking
  usePageViewTracking();
  
  const { isLoading: authLoading } = useAuth();

  // Show loading screen while checking auth
  if (authLoading) {
    return <AuthLoading />;
  }
  
  return (
    <>
      <Switch>
        <Route path="/auth" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <AuthLoading />;
          if (user) return <Redirect to="/games" />;
          return <AuthPage />;
        }} />
        <Route path="/admin" component={() => {
          const { user, isLoading } = useAuth();
          console.log("Admin Route Check - User:", user);
          console.log("Admin Route Check - isLoading:", isLoading);
          
          if (isLoading) {
            return (
              <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-border" />
              </div>
            );
          }
          
          if (!user) {
            console.log("Admin Route - No user, redirecting to auth");
            return <Redirect to="/auth" />;
          }
          
          if (user.role !== 'admin') {
            console.log("Admin Route - Not admin, user role is:", user.role);
            return <Redirect to="/" />;
          }
          
          console.log("Admin Route - Access granted to admin area");
          return <AdminPage />;
        }} />
        <Route path="/manage" component={() => {
          const { user, isLoading } = useAuth();
          console.log("Manager Route Check - User:", user);
          console.log("Manager Route Check - isLoading:", isLoading);
          
          if (isLoading) {
            return (
              <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-border" />
              </div>
            );
          }
          
          if (!user) {
            console.log("Manager Route - No user, redirecting to auth");
            return <Redirect to="/auth" />;
          }
          
          if (user.role !== 'manager' && user.role !== 'admin') {
            console.log("Manager Route - Not manager or admin, user role is:", user.role);
            return <Redirect to="/" />;
          }
          
          console.log("Manager Route - Access granted to manager area");
          return <ManagerPage />;
        }} />
        <Route path="/games" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <GamesPage />;
        }} />
        {/* Add notifications route */}
        <Route path="/notifications" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <NotificationsPage />;
        }} />
        <Route path="/messages" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <MessagesPage />;
        }} />
        <Route path="/messages/conversation/:userId" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <ConversationPage />;
        }} />
        <Route path="/profile" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <ProfilePage />;
        }} />
        <Route path="/game/:id" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) {
            return (
              <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-border" />
              </div>
            );
          }
          if (!user) {
            return <Redirect to="/auth" />;
          }
          return <GameRoom />;
        }} />
        <Route path="/watch" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <WatchPage />;
        }} />
        <Route path="/changelog" component={ChangeLog} />
        <Route path="/about" component={AboutPage} />
        <Route path="/game-history/:gameId" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <GameHistory />;
        }} />
        <Route path="/replay/:gameId/:roundId" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <ReplayPage />;
        }} />
        <Route path="/settings" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <SettingsPage />;
        }} />
        <Route path="/settings/user" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <UserSettingsPage />;
        }} />
        <Route path="/settings/themes" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <ThemesPage />;
        }} />
        <Route path="/user-settings" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <UserSettingsPage />;
        }} />
        <Route path="/deck-marketplace" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <DeckMarketplace />;
        }} />
        <Route path="/deck/:id" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <DeckDetailPage />;
        }} />
        <Route path="/board/:id" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <BoardDetailsPage />;
        }} />
        <Route path="/saved-decks" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <SavedDecksPage />;
        }} />
        <Route path="/achievements" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <Achievements />;
        }} />
        <Route path="/shop/payment-callback" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <PaymentCallbackPage />;
        }} />
        <Route path="/shop" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <ShopPage />;
        }} />
        {/* Redirect old /currency path to /shop */}
        <Route path="/currency">
          <Redirect to="/shop" />
        </Route>
        <Route path="/tournaments" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <TournamentsPage />;
        }} />
        <Route path="/tournament/:id" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <TournamentDetailPage />;
        }} />
        <Route path="/privacy-policy" component={PrivacyPolicyPage} />
        <Route path="/terms-of-service" component={TermsOfServicePage} />
        <Route path="/support" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <SupportPage />;
        }} />
        <Route path="/admin/support" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          if (user.role !== 'admin') return <Redirect to="/" />;
          return <AdminSupportPage />;
        }} />
        <Route path="/admin/support/:id" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          if (user.role !== 'admin') return <Redirect to="/" />;
          return <AdminSupportPage />;
        }} />
        
        <Route path="/admin/game-room/:id" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          if (user.role !== 'admin') return <Redirect to="/" />;
          return <AdminGameRoomPage />;
        }} />
        
        <Route path="/admin/tournament/:id" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          if (user.role !== 'admin') return <Redirect to="/" />;
          return <AdminTournamentPage />;
        }} />

        {/* Email verification confirmation page */}
        <Route path="/verify-email" component={VerificationConfirmation} />

        {/* Password reset page */}
        <Route path="/password-reset" component={() => {
          return <PasswordResetPage />;
        }} />

        {/* Email change page */}
        <Route path="/email-change" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <EmailChangePage />;
        }} />

        {/* Password change page */}
        <Route path="/password-change" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <PasswordChangePage />;
        }} />

        {/* Delete Account page */}
        <Route path="/delete-account" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <DeleteAccountPage />;
        }} />
        
        {/* Open Source Credits page */}
        <Route path="/open-source-credits" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <OpenSourceCredits />;
        }} />

        {/* Sound management */}
        <Route path="/settings/sounds" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          const { SoundManager } = require('./components/user/SoundManager');
          return <SoundManager />;
        }} />

        {/* Test route for sound system */}
        <Route path="/test/sound-system" component={() => {
          // Using dynamic import instead of require
          return <React.Suspense fallback={<div>Loading...</div>}>
            <SoundSystemTest />
          </React.Suspense>;
        }} />
        <Route path="/sound-test" component={() => {
          // No authentication needed for sound test page - direct import
          return <React.Suspense fallback={<div>Loading...</div>}>
            <SoundTest />
          </React.Suspense>;
        }} />
        <Route path="/direct-sound-test" component={() => {
          // Using dynamic import instead of require
          return <React.Suspense fallback={<div>Loading...</div>}>
            <DirectSoundTest />
          </React.Suspense>;
        }} />
        <Route path="/standalone-sound-test" component={() => {
          // Using dynamic import instead of require
          return <React.Suspense fallback={<div>Loading...</div>}>
            <StandaloneSoundTest />
          </React.Suspense>;
        }} />
        <Route path="/transaction/:reference" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <TransactionDetailPage />;
        }} />
        <Route path="/card-back-marketplace" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <CardBackMarketplace />;
        }} />
        <Route path="/rewards" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <RewardsPage />;
        }} />
        <Route path="/all-awards" component={() => {
          const { user, isLoading } = useAuth();
          if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
          if (!user) return <Redirect to="/auth" />;
          return <AllAwardsPage />;
        }} />
        
        <Route path="/404" component={NotFound} />
        <Route path="/">
          {() => {
            const { user, isLoading } = useAuth();
            if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>;
            return user ? <Redirect to="/games" /> : <LandingPage />;
          }}
        </Route>
        <Route path="/:rest*">
          <Redirect to="/404" />
        </Route>
      </Switch>
      <BottomNav />
    </>
  );
}

function App() {
  useEffect(() => {
    // Register service worker for PWA functionality
    registerServiceWorker();
    
    // Check for Firebase authentication redirects on app start
    checkRedirectResult().then(result => {
      if (result && result.user) {
        console.log('Authentication redirect result detected on app start:', result.user.displayName);
      }
    }).catch(error => {
      console.error('Error checking for auth redirects on app start:', error);
    });
    
    // Setup deeplink handler for mobile Paystack callbacks
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appUrlOpen', (data) => {
        console.log('App opened with URL:', data.url);
        const url = new URL(data.url);
        if (url.protocol === 'sparapp:') {
          console.log('Detected sparapp:// deeplink');
          const path = url.pathname.substring(2);
          const searchParams = new URLSearchParams(url.search);
          if (path === 'payment-callback') {
            const reference = searchParams.get('reference');
            const token = searchParams.get('token');
            if (reference && token) {
              window.location.href = `/shop/payment-callback?reference=${reference}&token=${token}`;
            } else {
              console.error('Missing parameters in payment callback deeplink');
              window.location.href = '/shop';
            }
          }
        }
      });
      console.log('Deeplink handler registered for Paystack payment callbacks');
    }

    const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';
    if (GA_MEASUREMENT_ID) {
      initGA(GA_MEASUREMENT_ID);
      console.log('Google Analytics initialized with ID from environment');
    } else {
      console.warn('Google Analytics Measurement ID is missing in environment variables');
    }
    
    // Global event handler for game_created events
    const handleGameCreated = (event: CustomEvent) => {
      try {
        if (event.detail && event.detail.gameId) {
          console.log("[App] Game created event received at App level:", event.detail);
          window.location.href = `/game/${event.detail.gameId}`;
        }
      } catch (error) {
        console.error("[App] Error handling game_created event:", error);
      }
    };
    
    window.addEventListener("game_created", handleGameCreated as EventListener);
    console.log("[App] Added global game_created event listener");
    
    return () => {
      window.removeEventListener("game_created", handleGameCreated as EventListener);
      console.log("[App] Removed global game_created event listener");
      if (Capacitor.isNativePlatform()) {
        CapApp.removeAllListeners();
      }
    };
  }, []);

  useEffect(() => {
    // Initialize sounds on app start
    initializeSounds();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <VersionCheckProvider>
          <AuthProvider>
            <NotificationProvider>
              <AchievementProvider>
                <ThemeProvider>
                  <AnnouncementBannerProvider>
                    <div className="min-h-screen pb-10 font-sans antialiased">
                      <WebSocketProvider>
                        <DeckStorageInitializer />
                        <Router />
                        <Toaster />
                        <QueueStatus />
                        <MatchDialog />
                        <TutorialSuggestionDialog />
                        <PushNotificationListener />
                      </WebSocketProvider>
                    </div>
                  </AnnouncementBannerProvider>
                </ThemeProvider>
              </AchievementProvider>
            </NotificationProvider>
          </AuthProvider>
        </VersionCheckProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;